import LZString from 'lz-string';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { memo } from 'react';

import { PagePropsType } from '@/common/Types';
import { decryptData, generateRandomNumber } from '@/common/utils/AppConstants';
import { getAppCookie } from '@/common/utils/cookies/getAppCookie';
import logger from '../../logger';

const AppWrapperNew = dynamic(() => import('@/common/components/AppWrapperNew'), { ssr: false });

const App = memo(({ pageProps, responseError, serviceUnavailable, redirectSlug, ...props }: PagePropsType) => (
	<AppWrapperNew
		pageProps={pageProps}
		responseError={responseError}
		serviceUnavailable={serviceUnavailable}
		redirectSlug={redirectSlug}
		{...props}
	/>
));

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
	logger.info('EA App Started..');

	const api_route = process.env.API_URL;
	const currentPage = Array.isArray(context?.params?.slug) ? context?.params?.slug?.join('/') : context?.params?.slug;

	let queryParams = context?.query;
	delete queryParams?.slug;
	delete queryParams?.error;
	delete queryParams?.error_description;
	if (queryParams && Object.entries(queryParams)?.length) {
		Object.entries(queryParams)?.forEach(([key, value]) => {
			queryParams[key] = decryptData(value as string, process.env.SECRET_PHRASE as string);
		})
	}

	const auth0Cookie = getAppCookie(context, 'auth0-session-');
	const campaignCookie = getAppCookie(context, 'campaign-');
	const eligibilityCookie = getAppCookie(context, 'eligibility-');
	const audienceCookie = getAppCookie(context, 'audience');

	const otherParameters: { [key: string]: string | number } = {};
	if (audienceCookie) {
		otherParameters.sessionRandom = audienceCookie?.randomNumber;
	}
	else {
		const randomNumber = generateRandomNumber();
		const compressedData = LZString.compressToBase64(JSON.stringify({ randomNumber }));
		context.res.setHeader(
			"Set-Cookie",
			`audience=${compressedData}; Path=/; HttpOnly; SameSite=Lax; secure: ${process.env.ENVIRONMENT !== 'Local'};`
		);
		otherParameters.sessionRandom = randomNumber
	}

	let response = null;
	let responseError = null;

	// Handle authentication callback errors
	if (currentPage?.startsWith('auth/callback')) {
		if (context?.query?.error) {
			responseError = {
				error: context?.query?.error,
				description: context?.query?.error_description,
			};
			logger.error('Authentication Callback Error', { error: responseError });
		}
	} else {
		// Fetch page data
		try {
			response = await fetch(`${api_route}/presentation/post/pageData`, {
				method: 'POST',
				// headers: headers, // Convert Headers back to a plain object
				credentials: 'omit',
				body: JSON.stringify({ slug: '/', auth: auth0Cookie, campaign: campaignCookie, ...eligibilityCookie, params: queryParams, others: otherParameters })
			});
		} catch (fetchError: any) {
			responseError = { error: 'Fetch error', description: fetchError?.message };
			logger.error('Fetch Error', { error: responseError });
		}
	}

	// Log any errors encountered during fetching
	if (responseError) {
		logger.error('Home Page Data getServerSideProps', { error: responseError });
	}

	let response_json: any | null = null;
	if (response?.ok) {
		response_json = await response.json();
	}

	// Determine redirect slug if available
	const redirect_slug = response_json?.data?.content?.[0]?.attributes?.targetedExperience?.[0]?.experienceEntry?.data?.attributes?.experienceMetadata?.pageRedirect ||
		response_json?.data?.content?.[0]?.attributes?.pageRedirect ||
		null;

	return {
		props: {
			serviceUnavailable: (typeof responseError === 'number' && responseError === 503) ? 'true' : process.env.ENABLE_SERVICE_DOWNTIME,
			pageProps: response_json,
			responseError: responseError || !response_json?.data?.content?.length || !response_json?.data?.content[0],
			redirectSlug: redirect_slug,
		},
	};
};

App.displayName = 'App';

export default App;